import { formatInTimeZone } from 'date-fns-tz';
import { parseZonedDateTime } from '@internationalized/date';
import {
  CompletedGroundCheckpointLogModel,
  DeviceTrackingLogType,
  DeviceTrackingLogTypeEnum,
  GroundCheckpointEtaLogModel,
  GroundTrackingCheckpointLogModel,
  GroundTrackingNodeLog,
  ModifiedGroundCheckpointLogModel,
} from '@/api/apis/route';
import { MS_IN_S } from '@/constants/date.constants';
import { getTzDateTimeString, getUserTimezone, convertToTimezoneOffset, yesNo } from '@/utils/helpers';
import { LOG_VALUE_NAME, DeviceTrackingValueEnum } from './consts';

export const getTrackingDevice = (leg: DeviceTrackingLogType) => {
  switch (leg.$type) {
    case DeviceTrackingLogTypeEnum.DeviceTrackingCreatedLogModel:
    case DeviceTrackingLogTypeEnum.DisplaySettingsModifiedLogModel:
    case DeviceTrackingLogTypeEnum.DisplaySettingsResetLogModel:
    case DeviceTrackingLogTypeEnum.ArrivalTimeRecalculatedLogModel:
      return null;
    default:
      return leg.device;
  }
};

const formatDateTimeString = (dateTimeString: string) => {
  const tz = getUserTimezone();
  const dateUTC = parseZonedDateTime(getTzDateTimeString(dateTimeString)).toAbsoluteString();

  return formatInTimeZone(dateUTC, tz, 'MM/dd/yyyy HH:mm:ss zzz');
};

const formatLogString = (type: string, prevValue: string, newValue: string) => {
  const newValueFormatted = newValue ? ` > ${newValue}` : '';

  return `${type}: ${prevValue}${newValueFormatted}`;
};

const valueFallback = (showNA: boolean) => (showNA ? 'N/A' : '');

const formatDateLog = (
  type: DeviceTrackingValueEnum,
  prevValue: string | null,
  newValue: string | null,
  isChangeLog: boolean,
) => {
  const typeName = LOG_VALUE_NAME[type];
  const prevDateTime = prevValue ? formatDateTimeString(prevValue) : 'N/A';
  const newDateTime = newValue ? formatDateTimeString(newValue) : valueFallback(isChangeLog);

  return formatLogString(typeName, prevDateTime, newDateTime);
};

const formatPrimitiveLog = (
  type: DeviceTrackingValueEnum,
  prevValue: string | null,
  newValue: string | null,
  isChangeLog: boolean,
) => {
  const typeName = LOG_VALUE_NAME[type];
  const prevInfo = prevValue || 'N/A';
  const newInfo = newValue || valueFallback(isChangeLog);

  return formatLogString(typeName, prevInfo, newInfo);
};

const formatWaitLog = (
  type: DeviceTrackingValueEnum,
  prevValue: number | null,
  newValue: number | null,
  isChangeLog: boolean,
) => {
  const typeName = LOG_VALUE_NAME[type];
  const leftWait = typeof prevValue === 'number' ? convertToTimezoneOffset(prevValue * MS_IN_S, false) : 'N/A';
  const rightWait =
    typeof newValue === 'number' ? convertToTimezoneOffset(newValue * MS_IN_S, false) : valueFallback(isChangeLog);

  return formatLogString(typeName, leftWait, rightWait);
};

const formatBoolLog = (
  type: DeviceTrackingValueEnum,
  prevValue: boolean | null,
  newValue: boolean | null,
  isChangeLog: boolean,
) => {
  const typeName = LOG_VALUE_NAME[type];
  const prevBool = typeof prevValue === 'boolean' ? yesNo(prevValue) : 'N/A';
  const newBool = typeof newValue === 'boolean' ? yesNo(newValue) : valueFallback(isChangeLog);

  return formatLogString(typeName, prevBool, newBool);
};

export const getLogString = (
  type: DeviceTrackingValueEnum,
  prevValue: string | number | boolean | null,
  newValue: string | number | boolean | null,
  isChangeLog: boolean = false,
): string | null => {
  switch (type) {
    case DeviceTrackingValueEnum.PTA:
    case DeviceTrackingValueEnum.STA:
    case DeviceTrackingValueEnum.ETA:
      return formatDateLog(type, prevValue as string | null, newValue as string | null, isChangeLog);
    case DeviceTrackingValueEnum.Device:
    case DeviceTrackingValueEnum.TailNumber:
    case DeviceTrackingValueEnum.LocationType:
    case DeviceTrackingValueEnum.TripNumber:
      return formatPrimitiveLog(type, prevValue as string | null, newValue as string | null, isChangeLog);
    case DeviceTrackingValueEnum.WaitDuration:
      return formatWaitLog(type, prevValue as number | null, newValue as number | null, isChangeLog);
    case DeviceTrackingValueEnum.IsCompleted:
    case DeviceTrackingValueEnum.DisplayLocationTracking:
    case DeviceTrackingValueEnum.DisplayETA:
      return formatBoolLog(type, prevValue as boolean | null, newValue as boolean | null, isChangeLog);
    default:
      return null;
  }
};

export const getLogInformation = (log: DeviceTrackingLogType): string[] => {
  switch (log.$type) {
    case DeviceTrackingLogTypeEnum.AirTrackingNodeAddedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TailNumber, log.tailNumber, null),
        getLogString(DeviceTrackingValueEnum.PTA, log.plannedArrivalTime, null),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.AirTrackingNodeEtaModifiedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TailNumber, log.tailNumber, null),
        getLogString(DeviceTrackingValueEnum.ETA, log.previousEstimatedArrivalTime, log.newEstimatedArrivalTime, true),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.AirTrackingNodeModifiedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TailNumber, log.previousTailNumber, log.newTailNumber, true),
        getLogString(DeviceTrackingValueEnum.PTA, log.previousPlannedArrivalTime, log.newPlannedArrivalTime, true),
        getLogString(DeviceTrackingValueEnum.STA, log.previousScheduledArrivalTime, log.newScheduledArrivalTime, true),
        getLogString(DeviceTrackingValueEnum.ETA, log.previousEstimatedArrivalTime, log.newEstimatedArrivalTime, true),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.AirTrackingNodeRemovedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TailNumber, log.tailNumber, null),
        getLogString(DeviceTrackingValueEnum.PTA, log.plannedArrivalTime, null),
        getLogString(DeviceTrackingValueEnum.STA, log.scheduledArrivalTime, null),
        getLogString(DeviceTrackingValueEnum.ETA, log.estimatedArrivalTime, null),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.AirTrackingNodeStaModifiedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TailNumber, log.tailNumber, null),
        getLogString(DeviceTrackingValueEnum.STA, log.previousScheduledArrivalTime, log.newScheduledArrivalTime, true),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.DeviceTrackingCreatedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.Device, log.deviceToDisplay, null),
        getLogString(DeviceTrackingValueEnum.DisplayETA, log.displayEstimatedTimeOfArrival, null),
        getLogString(DeviceTrackingValueEnum.DisplayLocationTracking, log.displayLocationTracking, null),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.DisplaySettingsModifiedLogModel:
    case DeviceTrackingLogTypeEnum.DisplaySettingsResetLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.Device, log.previousDeviceToDisplay, log.newDeviceToDisplay, true),
        getLogString(
          DeviceTrackingValueEnum.DisplayETA,
          log.previousDisplayEstimatedTimeOfArrival,
          log.newDisplayEstimatedTimeOfArrival,
          true,
        ),
        getLogString(
          DeviceTrackingValueEnum.DisplayLocationTracking,
          log.previousDisplayLocationTracking,
          log.newDisplayLocationTracking,
          true,
        ),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.GroundTrackingCheckpointCompletedLogModel:
      return [
        getLogString(DeviceTrackingValueEnum.TripNumber, log.tripNumber, null),
        getLogString(DeviceTrackingValueEnum.LocationType, log.checkpoint.locationType, null),
        getLogString(
          DeviceTrackingValueEnum.IsCompleted,
          log.checkpoint.previousIsCompleted,
          log.checkpoint.newIsCompleted,
          true,
        ),
        getLogString(
          DeviceTrackingValueEnum.ETA,
          log.checkpoint.previousEstimatedArrivalTime,
          log.checkpoint.newEstimatedArrivalTime,
          true,
        ),
      ].filter((s): s is string => Boolean(s));
    case DeviceTrackingLogTypeEnum.ArrivalTimeRecalculatedLogModel:
      return log.arrivalTimePerDevice
        .flatMap((deviceInfo) => [
          getLogString(DeviceTrackingValueEnum.Device, deviceInfo.device.fullSerialNumber, null),
          getLogString(
            DeviceTrackingValueEnum.STA,
            deviceInfo.previousScheduledArrivalTime,
            deviceInfo.newScheduledArrivalTime,
            true,
          ),
          getLogString(
            DeviceTrackingValueEnum.ETA,
            deviceInfo.previousEstimatedArrivalTime,
            deviceInfo.newEstimatedArrivalTime,
            true,
          ),
        ])
        .filter((s): s is string => Boolean(s));
    default:
      return [];
  }
};

export const getGroundNodeFlatInfo = (checkpoint: GroundTrackingCheckpointLogModel): string[] => {
  return [
    getLogString(DeviceTrackingValueEnum.LocationType, checkpoint.locationType, null),
    getLogString(DeviceTrackingValueEnum.IsCompleted, checkpoint.isCompleted, null),
    getLogString(DeviceTrackingValueEnum.WaitDuration, checkpoint.waitDurationInSeconds, null),
    getLogString(DeviceTrackingValueEnum.STA, checkpoint.scheduledArrivalTime, null),
    getLogString(DeviceTrackingValueEnum.ETA, checkpoint.estimatedArrivalTime, null),
  ].filter((s): s is string => Boolean(s));
};

export const getGroundNodeModifiedInfo = (checkpoint: ModifiedGroundCheckpointLogModel): string[] => {
  return [
    getLogString(
      DeviceTrackingValueEnum.LocationType,
      checkpoint.previousLocationType,
      checkpoint.newLocationType,
      true,
    ),
    getLogString(DeviceTrackingValueEnum.IsCompleted, checkpoint.previousIsCompleted, checkpoint.newIsCompleted, true),
    getLogString(
      DeviceTrackingValueEnum.WaitDuration,
      checkpoint.previousWaitDurationInSeconds,
      checkpoint.newWaitDurationInSeconds,
      true,
    ),
    getLogString(
      DeviceTrackingValueEnum.STA,
      checkpoint.previousScheduledArrivalTime,
      checkpoint.newScheduledArrivalTime,
      true,
    ),
  ].filter((s): s is string => Boolean(s));
};

export const getGroundNodeETAInfo = (checkpoint: GroundCheckpointEtaLogModel): string[] => {
  return [
    getLogString(DeviceTrackingValueEnum.LocationType, checkpoint.locationType, null),
    getLogString(
      DeviceTrackingValueEnum.ETA,
      checkpoint.previousEstimatedArrivalTime,
      checkpoint.newEstimatedArrivalTime,
      true,
    ),
  ].filter((s): s is string => Boolean(s));
};

export const getGroundNodeCompletedInfo = (checkpoint: CompletedGroundCheckpointLogModel): string[] => {
  return [
    getLogString(DeviceTrackingValueEnum.LocationType, checkpoint.locationType, null),
    getLogString(DeviceTrackingValueEnum.IsCompleted, checkpoint.previousIsCompleted, checkpoint.newIsCompleted, true),
    getLogString(
      DeviceTrackingValueEnum.ETA,
      checkpoint.previousEstimatedArrivalTime,
      checkpoint.newEstimatedArrivalTime,
      true,
    ),
  ].filter((s): s is string => Boolean(s));
};

export const isGroundTrackingNodeLog = (log: DeviceTrackingLogType): log is GroundTrackingNodeLog => {
  return [
    DeviceTrackingLogTypeEnum.GroundTrackingNodeAddedLogModel,
    DeviceTrackingLogTypeEnum.GroundTrackingNodeCompletedLogModel,
    DeviceTrackingLogTypeEnum.GroundTrackingNodeEtaModifiedLogModel,
    DeviceTrackingLogTypeEnum.GroundTrackingNodeModifiedLogModel,
    DeviceTrackingLogTypeEnum.GroundTrackingNodeRemovedLogModel,
  ].includes(log.$type);
};
